import api from '../api';
import { analytics } from '../analytics';

// ── Types (mirror backend PaymentInitResponse / OrderStatusResponse) ────────

export type PaymentMethod = 'card' | 'qr';
export type OrderStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export interface EpayPaymentObject {
  invoiceId: string;
  backLink: string;
  failureBackLink: string;
  postLink: string;
  failurePostLink: string;
  language: string;
  description: string;
  accountId: string;
  terminal: string;
  amount: number;
  currency: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  auth: Record<string, unknown>;
  jsUrl: string;
}

export interface EpayQrInfo {
  qrcode?: string;
  homebankLink?: string;
  onlinebankLink?: string;
  status?: string;
  billNumber?: string;
  [key: string]: unknown;
}

export interface PaymentInitResponse {
  invoice_id: string;
  method: PaymentMethod;
  payment?: EpayPaymentObject | null;
  qr?: EpayQrInfo | null;
  already_paid: boolean;
  fulfilled?: boolean;
}

export interface OrderStatusResponse {
  invoice_id: string;
  status: OrderStatus;
  fulfilled?: boolean;
  kind: 'purchase' | 'subscription';
  payment_type: PaymentMethod;
  amount: number;
  currency: string;
  /** Server-calculated reporting eligibility; absent legacy fields fail closed. */
  analytics_eligible?: boolean;
}

export interface CheckoutItemInput {
  item_type: string;
  quantity: number;
}

// ── API calls ─────────────────────────────────────────────────────────────

export async function checkoutServices(items: CheckoutItemInput[], method: PaymentMethod): Promise<PaymentInitResponse> {
  const { data } = await api.post<PaymentInitResponse>('/billing/checkout', { items, method });
  return data;
}

export async function subscribeToPlan(planId: string, saveCard: boolean = true): Promise<PaymentInitResponse> {
  // Subscriptions are card-only: QR payments can't be tokenized for auto-renewal.
  const { data } = await api.post<PaymentInitResponse>('/subscriptions/subscribe', { plan_id: planId, method: 'card', save_card: saveCard });
  return data;
}

// ── Saved cards (ePay tokens — card data itself lives at Halyk) ────────────

export interface SavedCard {
  id: string;
  card_mask: string;
  card_type: string;
  issuer: string | null;
  created_at: string;
}

export async function getSavedCards(): Promise<SavedCard[]> {
  const { data } = await api.get<SavedCard[]>('/billing/cards');
  return data;
}

export async function deleteSavedCard(cardId: string): Promise<void> {
  await api.delete(`/billing/cards/${cardId}`);
}

export async function getOrderStatus(invoiceId: string): Promise<OrderStatusResponse> {
  const { data } = await api.get<OrderStatusResponse>(`/billing/orders/status/${invoiceId}`);
  analytics.purchase(data);
  return data;
}

// ── ePay hosted Payment Page (full-page redirect) ────────────────────────────
// halyk.pay() itself navigates the browser away to the ePay payment page —
// this is the "redirect to hosted page" integration the JS bundle provides.

declare global {
  interface Window {
    halyk?: { pay: (paymentObject: Record<string, unknown>) => void };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadPayformScript(jsUrl: string): Promise<void> {
  if (window.halyk) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${jsUrl}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('epay_script_failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = jsUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('epay_script_failed'));
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/** Loads the ePay payform script (if needed) and redirects to the hosted payment page. */
export async function redirectToCardPayment(payment: EpayPaymentObject): Promise<void> {
  await loadPayformScript(payment.jsUrl);
  if (!window.halyk) throw new Error('epay_script_failed');
  const { jsUrl, ...paymentObject } = payment;
  void jsUrl;
  window.halyk.pay(paymentObject);
}
